import { useEffect, useState, useRef } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLTable from "application/components/CTLTable";
import { getColumns, Attributes, operationURLS } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import CTLSelectedDownload from "application/components/CTLSelectDownload";
import CTLNotification from "application/components/CTLNotification";
import { application } from "globals/endpoints";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";

export default function OutboundExtensionDetails({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName,
}) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, tableValue, tableSortColumn, tableColumnDataType } =
    controller;

  const [openFilter, setOpenFilter] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [columns, setColumns] = useState(getColumns());
  const [updateList, setUpdateList] = useState(Date.now());
  const [loading, setLoading] = useState(false);

  // Optional email delivery for scheduled pooled reports (task #9594). Captured in the page
  // because the CTLNavBar DownloadPdf/DownloadBillPdf callbacks carry no params — the schedule
  // request bodies read these on submit. Email ID/Name inputs are only shown when Send Email is on.
  const [sendEmail, setSendEmail] = useState(false);
  const [emailId, setEmailId] = useState("");
  const [emailName, setEmailName] = useState("");

  // The DownloadPdf/DownloadBillPdf callbacks are registered with CTLNavBar in a useEffect keyed on
  // [activePage, tableValue], so the navbar invokes a stale closure that captured the email state at
  // registration time (empty). Mirror the live values into refs and read them in buildEmailFields so
  // submit always sees the current Send Email / Email ID / Name regardless of when it was registered.
  const sendEmailRef = useRef(sendEmail);
  const emailIdRef = useRef(emailId);
  const emailNameRef = useRef(emailName);
  sendEmailRef.current = sendEmail;
  emailIdRef.current = emailId;
  emailNameRef.current = emailName;

  const filteredList = getColumns().filter(
    (item) => item.displayInList === true
  );
  const selectedColumnDownload = useRef(
    filteredList && filteredList.map((item) => item.accessor)
  );
  const [sortColumn, setSortColumn] = useState("callerIdNumber");
  const [columnDataType, setColumnDataType] = useState("string");
  useEffect(() => {
    {
      tableSortColumn != "" && setSortColumn(tableSortColumn);
    }
    {
      tableColumnDataType != "" && setColumnDataType(tableColumnDataType);
    }
  }, [tableSortColumn, tableColumnDataType]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const isMount = useRef(tableValue);

  // Getting the From Current Time
  const FromCurrentDateTime = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const isoString = `${year}-${month}-${day}`;
    return isoString;
  };

  // Getting the To Current Time
  const ToCurrentDateTime = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const isoString = `${year}-${month}-${day}`;
    return isoString;
  };
  const [advancedFilters, setAdvancedFilters] = useState([
    {
      direction: "from",
      dataType: "date",
      fieldName: "datetimeIst",
      value: FromCurrentDateTime(),
    },
    {
      direction: "to",
      dataType: "date",
      fieldName: "datetimeIst",
      value: ToCurrentDateTime(),
    },
  ]);

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        openForm: onOpenForm,
        DownloadData: onUpdateDataDownload,
        UpdateTableRecords: DownloadData,
        DownloadPdf: DownloadPdf,
        DownloadBillPdf: DownloadBillPdf,
        onRefresh: onRefresh,
      });
  }, [activePage, tableValue]);

  // Refresh Function
  const onRefresh = () => {
    setUpdateList(Date.now);
  };

  function DownloadData() {
    setOpenForm(true);
  }

  function onCloseForm() {
    setOpenForm(false);
  }

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onOpenForm() {
    setFormData({
      eventTime: Date.now(),
    });
    setOpenForm(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(advancedFilters) {
    setAdvancedFilters(advancedFilters);
    setOpenFilter(false);
  }

  const updateColumns = (selectedItems) => {
    const updatedColumns = getColumns().map((column) => {
      return {
        ...column,
        displayInList: selectedItems.includes(column.accessor),
      };
    });
    setColumns(updatedColumns);
    selectedColumnDownload.current = selectedItems;
  };

  // for download CSV Report
  function onUpdateDataDownload() {
    if (
      !selectedColumnDownload.current ||
      selectedColumnDownload.current.length === 0
    ) {
      return setNotify({
        isOpen: true,
        pagename: pageName,
        type: "warning",
        status: "You cann't download more that 10 column in pdf mode",
        message: "Please select 10 relevant column using Coumn Filer option",
      });
    }

    setLoading(true);
    const requestBodyData = {
      sortDirection: "asc",
      sortBy: sortColumn,
      search: searchText,
      sortDataType: columnDataType,
      columnNames: selectedColumnDownload.current,
      advancedFilters: advancedFilters,
    };
    const requestConfig = {
      responseType: "arraybuffer",
    };
    application
      .post(operationURLS.csvDownload, requestBodyData, requestConfig)
      .then((response) => {
        if (response) {
          setLoading(false);
          const blob = new Blob([response.data], { type: "text/csv" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "Outbound-Extension-details.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);

          setNotify({
            isOpen: true,
            pagename: pageName,
            status: "CSV Download successful",
            type: "success",
          });
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          setNotify({
            isOpen: true,
            pagename: pageName,
            status: "Unable To Download File",
            type: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  // Build the optional email-delivery fields for a schedule request body (task #9594). When Send
  // Email is unchecked the request omits these fields entirely (normal request). Returns null when
  // Send Email is on but no Email ID was entered — caller must abort and warn the user.
  function buildEmailFields() {
    // Read from refs (not the closed-over state) so the navbar's stale callback closure still sees
    // the current values entered after registration (task #9594 Fail fix).
    if (!sendEmailRef.current) {
      return {};
    }
    const trimmedId = emailIdRef.current.trim();
    // Reject empty or malformed addresses (task #9594 testing fix) — permissive x@y.z check.
    if (!trimmedId || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedId)) {
      return null;
    }
    return {
      sendEmail: true,
      emailId: trimmedId,
      emailName: emailNameRef.current.trim(),
    };
  }

  // Schedule a pooled billing report (task #9574/#9594). Both PDF actions are single-shot: the
  // request is accepted immediately and generated in the background. We do NOT poll or download in
  // place — the user tracks progress and downloads the finished PDF from Billing Reports → Scheduled
  // Reports (simplification per #9594). Optional email delivery is sent server-side when requested.
  function schedulePooledReport(reportType) {
    const emailFields = buildEmailFields();
    if (emailFields === null) {
      return setNotify({
        isOpen: true,
        pagename: pageName,
        type: "warning",
        status: "Please enter a valid Email ID to send the report by email.",
      });
    }

    setLoading(true);
    const requestBodyData = {
      sortDirection: "asc",
      sortBy: sortColumn,
      search: searchText,
      sortDataType: columnDataType,
      columnNames: selectedColumnDownload.current,
      advancedFilters: advancedFilters,
      ...emailFields,
    };
    const requestConfig = { params: { reportType: reportType } };

    application
      .post(operationURLS.pdfDownload, requestBodyData, requestConfig)
      .then(() => {
        setLoading(false);
        setNotify({
          isOpen: true,
          pagename: pageName,
          type: "success",
          status:
            "Report scheduled. You can track its progress and download the report once it is ready from Billing Reports → Scheduled Reports.",
        });
      })
      .catch((error) => {
        setLoading(false);
        const backendMessage =
          error.response && error.response.data && error.response.data.message;
        setNotify({
          isOpen: true,
          pagename: pageName,
          type:
            error.response && error.response.status === 429 ? "warning" : "error",
          status:
            backendMessage ||
            "Unable to schedule the report. Please try again later.",
        });
      });
  }

  // for Download pdf — schedules the pooled normal report (ExtensionDetailsInPdf).
  function DownloadPdf() {
    if (
      !selectedColumnDownload.current ||
      selectedColumnDownload.current.length === 0
    ) {
      return setNotify({
        isOpen: true,
        pagename: pageName,
        type: "warning",
        status: "You cann't download more that 10 column in pdf mode",
        message: "Please select 10 relevant column using Coumn Filer option",
      });
    } else if (selectedColumnDownload.current.length > 10) {
      return setNotify({
        isOpen: true,
        pagename: pageName,
        type: "error",
        status: "Select maximum of 10 columns",
      });
    }
    schedulePooledReport("ExtensionDetailsInPdf");
  }

  // for Download Bill PDF (per-extension bill format) — schedules the pooled bill report.
  function DownloadBillPdf() {
    schedulePooledReport("outboundExtensionDetailsBillPdf");
  }

  return (
    <MDBox>
      <CTLSelectedDownload
        openForm={openForm}
        columns={getColumns()}
        pageName={pageName}
        onCloseForm={onCloseForm}
        onUpdate={updateColumns}
      />
      <MDBox px={1} pb={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              inputProps={{ "aria-label": "Send Email" }}
            />
          }
          label={
            <MDTypography variant="button" fontWeight="regular">
              Send Email
            </MDTypography>
          }
        />
        {sendEmail && (
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={4}>
              <MDInput
                type="email"
                label="Email ID"
                fullWidth
                required
                value={emailId}
                onChange={(e) => setEmailId(e.target.value)}
                inputProps={{ "aria-label": "Email ID" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MDInput
                type="text"
                label="Name"
                fullWidth
                value={emailName}
                onChange={(e) => setEmailName(e.target.value)}
                inputProps={{ "aria-label": "Email Name" }}
              />
            </Grid>
          </Grid>
        )}
      </MDBox>
      <CTLTable
        apiURL={operationURLS.list}
        advancedFilters={advancedFilters}
        columns={columns}
        entriesPerPage={false}
        canSearch
        defaultSortDirection="asc"
        defaultSortColumn="callerIdNumber"
        pageId={pageId}
        searchText={searchText}
        hasRowSelection={false}
        updateList={updateList}
        defaultPageSize={20}
      />
      <MDBox
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CTLAdvancedFilter
          entityName={pageName}
          columns={getColumns()}
          applyFilter={openFilter}
          onApplyFilter={onApplyFilter}
          onCloseFilter={onCloseFilter}
          advancedFilters={advancedFilters}
        />
      </MDBox>
      <MDBox>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </MDBox>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}
